import { Link } from "react-router-dom";
import { ArrowRight, ChevronDown, MapPin, Plane } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { AIRPORTS, CITIES, S, lp, p } from "@/lib/seoContent";

// Bloc Destinations (maillage SEO local) — villes principales + aéroports,
// volontairement limité : le hub « Chauffeur privé » permet de tout découvrir.
const FOOTER_CITIES = ["geneve", "lausanne", "zurich", "montreux", "bern", "basel"];
const DEST_LABEL = {
  fr: { title: "Destinations", airports: "Aéroports", all: "Toutes les destinations" },
  en: { title: "Destinations", airports: "Airports", all: "All destinations" },
  de: { title: "Destinationen", airports: "Flughäfen", all: "Alle Destinationen" },
};

const HREFS = [
  ["/services", "/reservation", "/aeroports", "/flotte", "/villes"],
  ["/business", "/business/app", "/business"],
  ["/chauffeurs", "/faq", "/contact", "/guides"],
];

export default function Footer() {
  const { t, lang } = useLang();
  const cols = t("footer.cols");
  const links = t("footer.links");
  const legal = t("footer.legal");

  return (
    <footer className="bg-miles-deep" data-testid="footer">
      <div className="mx-auto max-w-7xl px-5 pb-10 pt-16 sm:px-8 lg:pt-24">
        <div className="grid gap-14 lg:grid-cols-[1.15fr_1fr] lg:gap-20">
          <div>
            <img src="/brand/logo-white.webp" alt="MILES" data-testid="footer-logo"
              className="h-12 w-auto select-none sm:h-14" draggable="false" />
            <p className="mt-5 max-w-sm text-base leading-relaxed text-miles-sky/70">{t("footer.tag")}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/reservation" data-testid="footer-booking-cta"
                className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-semibold text-miles-deep transition-all duration-300 hover:-translate-y-0.5">
                {t("footer.book")} <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
              </Link>
              <Link to={lp(lang, "/business")} data-testid="footer-business-cta"
                className="inline-flex items-center gap-2 rounded-full border border-miles-sky/30 px-6 py-3 text-sm font-semibold text-white transition-colors duration-300 hover:border-miles-sky">
                {t("footer.business")}
              </Link>
            </div>
            <div className="mt-10 flex flex-col items-start gap-1.5 text-sm">
              <a href="mailto:info@gomiles.ch" data-testid="footer-email-link"
                className="text-miles-sky/60 transition-colors duration-300 hover:text-white">info@gomiles.ch</a>
              <a href="https://www.gomiles.ch" target="_blank" rel="noreferrer" data-testid="footer-website-link"
                className="text-miles-sky/60 transition-colors duration-300 hover:text-white">www.gomiles.ch</a>
            </div>
          </div>

          <div className="sm:hidden" data-testid="footer-nav-mobile">
            {cols.map((title, i) => (
              <details key={title} className="group border-b border-white/10" data-testid={`footer-accordion-${i}`}>
                <summary className="flex cursor-pointer list-none items-center justify-between py-5 [&::-webkit-details-marker]:hidden">
                  <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/80">{title}</span>
                  <ChevronDown className="h-4 w-4 text-miles-sky/60 transition-transform duration-300 group-open:rotate-180" strokeWidth={1.5} />
                </summary>
                <ul className="space-y-3.5 pb-6">
                  {links[i].map((label, j) => (
                    <li key={label}>
                      <Link to={lp(lang, HREFS[i][j])} className="text-sm text-white/65 transition-colors hover:text-white">{label}</Link>
                    </li>
                  ))}
                </ul>
              </details>
            ))}
          </div>

          <div className="hidden sm:grid sm:grid-cols-3 sm:gap-10 lg:justify-items-end" data-testid="footer-nav-desktop">
            {cols.map((title, i) => (
              <div key={title}>
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-miles-sky/45">{title}</p>
                <ul className="mt-5 space-y-3">
                  {links[i].map((label, j) => (
                    <li key={label}>
                      <Link to={lp(lang, HREFS[i][j])} className="text-sm text-white/65 transition-colors duration-300 hover:text-white">
                        {label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-14 border-t border-white/10 pt-8" data-testid="footer-destinations">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-miles-sky/45">{DEST_LABEL[lang].title}</p>
          <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2.5">
            {FOOTER_CITIES.map((cid) => (
              <Link key={cid} to={p(lang, S.chauffeur[lang], CITIES[cid].slug[lang])} data-testid={`footer-city-${cid}`}
                className="inline-flex items-center gap-1.5 text-sm text-white/65 transition-colors duration-300 hover:text-white">
                <MapPin className="h-3 w-3 text-miles-sky/50" strokeWidth={1.5} /> {CITIES[cid].name[lang]}
              </Link>
            ))}
            <Link to={p(lang, S.chauffeur[lang])} data-testid="footer-all-destinations"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-miles-sky transition-colors duration-300 hover:text-white">
              {DEST_LABEL[lang].all} <ArrowRight className="h-3 w-3" strokeWidth={1.5} />
            </Link>
          </div>
          <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2.5">
            {Object.values(AIRPORTS).map((a) => (
              <Link key={a.id} to={p(lang, S.airport[lang], a.slug[lang])} data-testid={`footer-airport-${a.id}`}
                className="inline-flex items-center gap-1.5 text-sm text-white/65 transition-colors duration-300 hover:text-white">
                <Plane className="h-3 w-3 text-miles-sky/50" strokeWidth={1.5} /> {a.name[lang]}
              </Link>
            ))}
          </div>
        </div>

        <div className="mt-16 flex flex-wrap items-center justify-between gap-4 border-t border-white/10 pt-7">
          <p className="text-xs text-miles-sky/45">{t("footer.copy")}</p>
          <div className="flex items-center gap-6">
            {legal.map((l) => (
              <Link key={l} to="/contact" className="text-xs text-miles-sky/45 transition-colors hover:text-white">{l}</Link>
            ))}
            <Link to="/admin" data-testid="footer-admin-link" className="text-xs text-miles-sky/25 transition-colors hover:text-miles-sky/70">Admin</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
